import React from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import type { QrShareDetail } from '../../../types/api';
import { downloadQrPng, downloadQrSvg } from '../lib/qrExport';
import { buildPublicShareUrl } from '../lib/publicShareUrl';
import { QrCodeSvg } from './QrCodeSvg';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
  text-align: center;
`;

const Summary = styled.p`
  margin: 0;
  color: var(--text-secondary);
`;

const UrlBox = styled.p`
  margin: 0;
  padding: var(--space-sm) var(--space-md);
  width: 100%;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-sm, 6px);
  background: var(--surface-strong, #f8fafc);
  color: var(--text-primary);
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-sm);
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-xs);
`;

export interface QrShareResultPanelProps {
  share: QrShareDetail;
}

export const QrShareResultPanel: React.FC<QrShareResultPanelProps> = ({
  share,
}) => {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const shareUrl = React.useMemo(
    () => buildPublicShareUrl(share.public_token),
    [share.public_token],
  );

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace.');
    }
  }, [shareUrl]);

  const handleDownloadPng = React.useCallback(() => {
    if (!canvasRef.current) return;
    try {
      downloadQrPng(canvasRef.current, share.name, shareUrl);
    } catch {
      toast.error('No se pudo generar el PNG.');
    }
  }, [share.name, shareUrl]);

  const handleDownloadSvg = React.useCallback(() => {
    if (!svgRef.current) return;
    downloadQrSvg(svgRef.current, share.name);
  }, [share.name]);

  return (
    <Panel>
      <QrCodeSvg value={shareUrl} svgRef={svgRef} canvasRef={canvasRef} />

      <Summary>
        {share.item_count} {share.item_count === 1 ? 'archivo' : 'archivos'} en
        «{share.name}»
      </Summary>

      <UrlBox data-testid="qr-share-url">{shareUrl}</UrlBox>

      <Actions>
        <Button variant="primary" size="sm" onClick={handleCopy}>
          Copiar enlace
        </Button>
        <Button variant="secondary" size="sm" onClick={handleDownloadPng}>
          Descargar PNG
        </Button>
        <Button variant="secondary" size="sm" onClick={handleDownloadSvg}>
          Descargar SVG
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          as="a"
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Ver página pública
        </Button>
      </Actions>
    </Panel>
  );
};
